import type { AppointmentReason, InsightCheck, ReasonSentence, ReasonsData } from './types';

/** Fifteen bookable visit types, exactly as the SettingsReasons board draws them. */
const REASONS: AppointmentReason[] = [
  {
    id: 'r_npx', code: 'NPX', displayName: 'New Patient Exam', category: 'diagnostic',
    durationMinutes: 90, bufferAfterMinutes: 0, earliestBookable: 'Next open slot',
    roles: ['general_dentist'], bookableBy: 'anyone', cdtCodes: ['D0150'], bookable: true,
    patientDescription: 'A first visit — a full look around, x-rays if you need them, and a plan.',
  },
  {
    id: 'r_emg', code: 'EMG', displayName: 'Emergency Visit', category: 'emergency',
    durationMinutes: 30, bufferAfterMinutes: 0, earliestBookable: 'Next open slot',
    roles: ['general_dentist'], bookableBy: 'anyone', cdtCodes: [], bookable: true,
  },
  {
    id: 'r_hyg', code: 'HYG', displayName: 'Adult Cleaning', category: 'hygiene',
    durationMinutes: 60, bufferAfterMinutes: 0, earliestBookable: 'Next open slot',
    roles: ['hygienist'], bookableBy: 'anyone', cdtCodes: ['D1110'], bookable: true,
  },
  {
    id: 'r_hygc', code: 'HYG-C', displayName: 'Child Cleaning', category: 'hygiene',
    durationMinutes: 45, bufferAfterMinutes: 0, earliestBookable: 'Next open slot',
    roles: ['hygienist'], bookableBy: 'anyone', cdtCodes: ['D1120'], bookable: true,
  },
  {
    id: 'r_per', code: 'PER', displayName: 'Perio Maintenance', category: 'periodontic',
    durationMinutes: 60, bufferAfterMinutes: 0, earliestBookable: 'Next open slot',
    roles: ['hygienist', 'periodontist'], bookableBy: 'elva_and_staff', cdtCodes: ['D4910'], bookable: true,
  },
  {
    id: 'r_wht', code: 'WHT', displayName: 'Whitening', category: 'hygiene',
    durationMinutes: 45, bufferAfterMinutes: 0, earliestBookable: 'Next open slot',
    roles: ['hygienist', 'general_dentist'], bookableBy: 'anyone', cdtCodes: [], bookable: true,
  },
  {
    id: 'r_crp', code: 'CR-P', displayName: 'Crown Prep', category: 'restorative',
    durationMinutes: 90, bufferAfterMinutes: 0, earliestBookable: 'Next open slot',
    roles: ['general_dentist'], bookableBy: 'elva_and_staff', cdtCodes: ['D2740'], bookable: true,
  },
  {
    id: 'r_crs', code: 'CR-S', displayName: 'Crown Seat', category: 'restorative',
    durationMinutes: 45, bufferAfterMinutes: 0, earliestBookable: 'Next open slot',
    roles: ['general_dentist'], bookableBy: 'elva_and_staff', cdtCodes: ['D2740'], bookable: true,
    patientDescription:
      'Fitting the permanent crown made at your last visit. Usually quick and freezing is rarely needed.',
    elvaDescription:
      'Use when the patient already had a crown prepared and the lab work is back. If they describe a broken or loose crown with no prep on file, that is an Emergency Visit instead.',
  },
  {
    id: 'r_fil', code: 'FIL', displayName: 'Filling', category: 'restorative',
    durationMinutes: 45, bufferAfterMinutes: 0, earliestBookable: 'Next open slot',
    roles: ['general_dentist'], bookableBy: 'elva_and_staff', cdtCodes: ['D2391'], bookable: true,
  },
  {
    id: 'r_rct', code: 'RCT', displayName: 'Root Canal', category: 'endodontic',
    durationMinutes: 75, bufferAfterMinutes: 0, earliestBookable: 'Next open slot',
    roles: ['general_dentist', 'endodontist'], bookableBy: 'elva_and_staff', cdtCodes: ['D3330'], bookable: true,
  },
  {
    id: 'r_ext', code: 'EXT', displayName: 'Extraction', category: 'surgical',
    durationMinutes: 45, bufferAfterMinutes: 0, earliestBookable: 'Next open slot',
    roles: ['general_dentist', 'oral_surgeon'], bookableBy: 'staff_only', cdtCodes: [], bookable: true,
  },
  {
    id: 'r_impc', code: 'IMP-C', displayName: 'Implant Consult', category: 'surgical',
    durationMinutes: 60, bufferAfterMinutes: 0, earliestBookable: 'Next open slot',
    roles: ['periodontist', 'oral_surgeon'], bookableBy: 'anyone', cdtCodes: ['D6190'], bookable: true,
  },
  {
    id: 'r_impp', code: 'IMP-P', displayName: 'Implant Placement', category: 'surgical',
    durationMinutes: 120, bufferAfterMinutes: 0, earliestBookable: 'Next open slot',
    roles: ['periodontist', 'oral_surgeon'], bookableBy: 'staff_only', cdtCodes: ['D6010'], bookable: true,
  },
  {
    id: 'r_ortc', code: 'ORT-C', displayName: 'Invisalign Consult', category: 'orthodontic',
    durationMinutes: 45, bufferAfterMinutes: 0, earliestBookable: 'Next open slot',
    roles: ['orthodontist'], bookableBy: 'anyone', cdtCodes: ['D8090'], bookable: true,
  },
  {
    id: 'r_orta', code: 'ORT-A', displayName: 'Invisalign Adjustment', category: 'orthodontic',
    durationMinutes: 20, bufferAfterMinutes: 0, earliestBookable: 'Next open slot',
    roles: ['orthodontist'], bookableBy: 'anyone', cdtCodes: [], bookable: true,
  },
];

/** Sixteen sentences mapped by hand; two carry a condition, and those are the valuable ones. */
const SENTENCES: ReasonSentence[] = [
  { id: 's1', reasonId: 'r_emg', text: 'I think my crown fell off last night', source: 'written_here', author: 'Maria' },
  {
    id: 's2', reasonId: 'r_crs', text: 'The temporary came off but it doesn’t hurt',
    condition: 'there is an open crown prep on the chart', source: 'corrected_from_call', author: 'Maria',
  },
  { id: 's3', reasonId: 'r_crs', text: 'My crown is back from the lab — when can I come in?', source: 'written_here', author: 'Maria' },
  { id: 's4', reasonId: 'r_npx', text: 'I need a check-up, I’ve never been to you before', source: 'written_here', author: 'Tom' },
  { id: 's5', reasonId: 'r_hyg', text: 'Just my regular check-up', source: 'written_here', author: 'Tom' },
  { id: 's6', reasonId: 'r_hygc', text: 'My son needs his teeth cleaned', source: 'written_here', author: 'Maria' },
  { id: 's7', reasonId: 'r_emg', text: 'I’ve got a really bad toothache', source: 'confirmed_from_call', author: 'Tom' },
  { id: 's8', reasonId: 'r_emg', text: 'My face is swollen on one side', source: 'written_here', author: 'Maria' },
  { id: 's9', reasonId: 'r_hyg', text: 'It’s been about six months since I was in', source: 'written_here', author: 'Tom' },
  { id: 's10', reasonId: 'r_per', text: 'I’m due for my deep cleaning', source: 'confirmed_from_call', author: 'Maria' },
  {
    id: 's11', reasonId: 'r_fil', text: 'I’ve got a hole in one of my back teeth',
    condition: 'the patient is an existing patient', source: 'written_here', author: 'Tom',
  },
  { id: 's12', reasonId: 'r_wht', text: 'I want my teeth whitened before the wedding', source: 'written_here', author: 'Maria' },
  { id: 's13', reasonId: 'r_ortc', text: 'I’m interested in clear aligners', source: 'written_here', author: 'Tom' },
  { id: 's14', reasonId: 'r_orta', text: 'I need my next set of aligners', source: 'written_here', author: 'Maria' },
  { id: 's15', reasonId: 'r_rct', text: 'My dentist said I might need a root canal', source: 'written_here', author: 'Tom' },
  { id: 's16', reasonId: 'r_impc', text: 'I’m missing a tooth and want to know my options', source: 'written_here', author: 'Maria' },
];

export const REASONS_DATA: ReasonsData = {
  reasons: REASONS,
  sentences: SENTENCES,
  waitingFromCalls: 5,
  waitingCloseCall: 2,
  waitingNothingNear: 3,
};

/** Thirty-nine checks across three views. The three lists are deliberately different. */
export const INSIGHT_CHECKS: InsightCheck[] = [
  // ── Daily (11, 7 on) ──
  { id: 'd1', view: 'daily', group: 'revenue', label: 'Overall day business and flow', enabled: true },
  { id: 'd2', view: 'daily', group: 'revenue', label: 'Provider workload distribution', enabled: true },
  { id: 'd3', view: 'daily', group: 'revenue', label: 'New patients today', enabled: true },
  { id: 'd4', view: 'daily', group: 'retention', label: 'Hygiene and recare completion', enabled: false },
  { id: 'd5', view: 'daily', group: 'retention', label: 'Family overdue booking', enabled: true },
  { id: 'd6', view: 'daily', group: 'retention', label: 'Same-day retention nudge', enabled: false },
  { id: 'd7', view: 'daily', group: 'clinical', label: 'Potential scheduling bottlenecks', enabled: true },
  { id: 'd8', view: 'daily', group: 'clinical', label: 'Emergency and special-needs slots', enabled: true },
  { id: 'd9', view: 'daily', group: 'financial', label: 'Same-day and family upsell prep', enabled: false },
  { id: 'd10', view: 'daily', group: 'financial', label: 'Quick financial prep reminder', enabled: false },
  { id: 'd11', view: 'daily', group: 'financial', label: 'Day-end follow-up focus', enabled: true },
  // ── Weekly (14, 12 on) ──
  { id: 'w1', view: 'weekly', group: 'revenue', label: 'Week production projection vs goal', enabled: true },
  { id: 'w2', view: 'weekly', group: 'revenue', label: 'Provider load balancing', enabled: true },
  { id: 'w3', view: 'weekly', group: 'revenue', label: 'Open chair hours', enabled: true },
  { id: 'w4', view: 'weekly', group: 'revenue', label: 'Big-case treatment plans this week', enabled: true },
  { id: 'w5', view: 'weekly', group: 'retention', label: 'Hygiene re-appointment rate', enabled: false },
  { id: 'w6', view: 'weekly', group: 'retention', label: 'Family chain opportunities', enabled: true },
  { id: 'w7', view: 'weekly', group: 'retention', label: 'Long-gap returner re-engagement', enabled: true },
  { id: 'w8', view: 'weekly', group: 'clinical', label: 'Lab case ordering deadlines', enabled: true },
  { id: 'w9', view: 'weekly', group: 'clinical', label: 'Pre-auth pending follow-ups', enabled: true },
  { id: 'w10', view: 'weekly', group: 'clinical', label: 'Paediatric consent verifications', enabled: true },
  { id: 'w11', view: 'weekly', group: 'clinical', label: 'Patients arriving without forms', enabled: true },
  { id: 'w12', view: 'weekly', group: 'financial', label: 'Patients with benefits expiring', enabled: true },
  { id: 'w13', view: 'weekly', group: 'financial', label: 'Deductible met, nothing scheduled', enabled: false },
  { id: 'w14', view: 'weekly', group: 'financial', label: 'Weekly collections forecast', enabled: true },
  // ── Monthly (14, 12 on) ──
  { id: 'm1', view: 'monthly', group: 'revenue', label: 'Production gap to monthly goal', enabled: true },
  { id: 'm2', view: 'monthly', group: 'revenue', label: 'Daily average needed to close the gap', enabled: true },
  { id: 'm3', view: 'monthly', group: 'revenue', label: 'Per-provider goal attainment', enabled: true },
  { id: 'm4', view: 'monthly', group: 'retention', label: 'Hygiene re-appointment rate vs goal', enabled: true },
  { id: 'm5', view: 'monthly', group: 'retention', label: 'Total unscheduled treatment plan value', enabled: true },
  { id: 'm6', view: 'monthly', group: 'retention', label: 'Patient birthdays and milestones', enabled: true },
  { id: 'm7', view: 'monthly', group: 'retention', label: 'Referrals not yet contacted', enabled: true },
  { id: 'm8', view: 'monthly', group: 'clinical', label: 'Licence renewals due this month', enabled: true },
  { id: 'm9', view: 'monthly', group: 'clinical', label: 'OSHA log review reminder', enabled: true },
  { id: 'm10', view: 'monthly', group: 'clinical', label: 'Other compliance deadlines', enabled: false },
  { id: 'm11', view: 'monthly', group: 'financial', label: 'Aging A/R review', enabled: true },
  { id: 'm12', view: 'monthly', group: 'financial', label: 'Patients with unused benefits', enabled: true },
  { id: 'm13', view: 'monthly', group: 'financial', label: 'Insurance write-offs', enabled: false },
  { id: 'm14', view: 'monthly', group: 'financial', label: 'Unsigned treatment plans by value', enabled: true },
];
